/**
 * Виджет практики на «Дом» · гибрид «Верстак дня + подсказки бадди» + «Ритм недели».
 * 10 JUL 2026 · мокап: 04_DESIGN/mockup_home_homework_hybrid.html (ack Genie).
 * Тексты и вариативность: lib/nudges.ts по спеке
 * COORDINATION/specs/home_practice_widget_copy.md [ack · решения 1в/2а/3а].
 *
 * Чисто презентационные компоненты — вся деривация в Dashboard.tsx:
 *  - NudgeCard — событийная подсказка бадди (слот в верстаке · копия над планом на мобильном);
 *  - PracticeBench — карточка «Твоя практика» (подсказка + рекомендация + ось + хвост);
 *  - WeekRhythm — низкая карточка «Ритм недели» под планом.
 * Иконография — рисованные SVG-глифы (без эмодзи и icon-библиотек).
 */

import { BuddyMascot, type BuddyVariant } from '@/components/buddies';
import type { Nudge } from '@/lib/nudges';

// ── глифы ─────────────────────────────────────────────────────────────

function BenchGlyph() {
  return (
    <svg className="dpb-g" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3 14 l7 -10 7 10 z" />
      <path d="M7 14 v3 M13 14 v3 M3 17 h14" />
    </svg>
  );
}

function StarGlyph() {
  return (
    <svg className="dpb-g" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M10 2 l2 5 5 .5 -4 3.5 1.5 5 -4.5 -3 -4.5 3 1.5 -5 -4 -3.5 5 -.5 z" />
    </svg>
  );
}

function LampGlyph() {
  return (
    <svg className="dpb-g" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="7" r="4.5" />
      <path d="M10 11.5 v3 M8 17 h4" />
    </svg>
  );
}

// ── подсказка бадди ───────────────────────────────────────────────────

export interface NudgeCardProps {
  nudge: Nudge;
  buddyVariant: BuddyVariant;
  onAction: () => void;
  onDismiss: () => void;
}

export function NudgeCard({ nudge, buddyVariant, onAction, onDismiss }: NudgeCardProps) {
  return (
    <div className={`dpn-card dpn-${nudge.type}`} data-testid="dpn-card" data-nudge-id={nudge.id}>
      <span className="dpn-buddy" aria-hidden="true">
        <BuddyMascot variant={buddyVariant} size={30} />
      </span>
      <div className="dpn-body">
        <div className="dpn-kind">{nudge.kindLabel}</div>
        <p className="dpn-msg">{nudge.text}</p>
        <button type="button" className="dpn-act" data-testid="dpn-action" onClick={onAction}>
          {nudge.cta}
        </button>
      </div>
      <button
        type="button"
        className="dpn-x"
        data-testid="dpn-dismiss"
        aria-label="Скрыть подсказку"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

// ── верстак ───────────────────────────────────────────────────────────

export interface BenchRec {
  kind: 'resume' | 'fresh' | 'tail';
  chipLabel: string;
  code: string;
  estimateMin: number;
  title: string;
  /** только для resume */
  selfCheckDone?: number;
  selfCheckTotal?: number;
  reason: string;
  ctaLabel: string;
}

export interface BenchWeakAxis {
  label: string;
  done: number;
  total: number;
  /** 0-100 */
  pct: number;
}

export interface PracticeBenchProps {
  sprintLabel: string;
  doneCount: number;
  totalCount: number;
  /** null = тихий день · слот схлопнут */
  nudge: Nudge | null;
  buddyVariant: BuddyVariant;
  /** null = R5 «всё чисто» */
  rec: BenchRec | null;
  /** фраза пустого состояния (R5) */
  clearText: string;
  weakAxis: BenchWeakAxis | null;
  tail: { count: number; sprintId: string } | null;
  onOpenPractice: () => void;
  onNudgeAction: () => void;
  onNudgeDismiss: () => void;
}

export function PracticeBench({
  sprintLabel,
  doneCount,
  totalCount,
  nudge,
  buddyVariant,
  rec,
  clearText,
  weakAxis,
  tail,
  onOpenPractice,
  onNudgeAction,
  onNudgeDismiss,
}: PracticeBenchProps) {
  return (
    <aside className="dpb-card" data-testid="practice-bench" aria-label="Твоя практика">
      <div className="dpb-head">
        <BenchGlyph />
        <span className="dpb-t">Твоя практика</span>
        <span className="dpb-count">
          {sprintLabel} · {doneCount}/{totalCount}
        </span>
      </div>

      {nudge && (
        <div className="dpb-nudge-slot">
          <NudgeCard
            nudge={nudge}
            buddyVariant={buddyVariant}
            onAction={onNudgeAction}
            onDismiss={onNudgeDismiss}
          />
        </div>
      )}

      {rec ? (
        <div className="dpb-rec" data-testid="dpb-rec" data-rec-kind={rec.kind}>
          <span className={`dpb-chip dpb-chip-${rec.kind}`}>{rec.chipLabel}</span>
          <span className="dpb-tag">
            {rec.code} · ~{rec.estimateMin} мин
          </span>
          <div className="dpb-rec-t">{rec.title}</div>
          {rec.kind === 'resume' && rec.selfCheckTotal ? (
            <>
              <div
                className="dpb-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={rec.selfCheckTotal}
                aria-valuenow={rec.selfCheckDone ?? 0}
                aria-label="Чеклист самопроверки"
              >
                <i
                  style={{
                    width: `${Math.round(((rec.selfCheckDone ?? 0) / rec.selfCheckTotal) * 100)}%`,
                  }}
                />
              </div>
              <div className="dpb-bar-m">
                чеклист самопроверки · {rec.selfCheckDone ?? 0} из {rec.selfCheckTotal}
              </div>
            </>
          ) : null}
          <div className="dpb-why">
            <LampGlyph />
            <span>{rec.reason}</span>
          </div>
          <button
            type="button"
            className="btn-primary dpb-cta"
            data-testid="dpb-cta"
            onClick={onOpenPractice}
          >
            {rec.ctaLabel}
          </button>
        </div>
      ) : (
        <div className="dpb-clear" data-testid="dpb-clear">
          <div className="dpb-rec-t">Практика закрыта</div>
          <p className="dpb-clear-m">{clearText}</p>
          <button
            type="button"
            className="btn-primary dpb-cta"
            data-testid="dpb-cta"
            onClick={onOpenPractice}
          >
            В хаб практики →
          </button>
        </div>
      )}

      {weakAxis && (
        <div className="dpb-axis" data-testid="dpb-axis">
          <StarGlyph />
          <span className="dpb-axis-t">
            Слабая ось: <b>{weakAxis.label}</b>
          </span>
          <div className="dpb-bar dpb-bar-axis" aria-hidden="true">
            <i style={{ width: `${weakAxis.pct}%` }} />
          </div>
          <span className="dpb-axis-n">
            {weakAxis.done}/{weakAxis.total}
          </span>
        </div>
      )}

      {tail && (
        <div className="dpb-tail" data-testid="dpb-tail">
          <span>
            Хвост {tail.sprintId}: <b>{tail.count} {pluralHw(tail.count)}</b>
          </span>
          <button type="button" className="dpb-tail-link" onClick={onOpenPractice}>
            к хвосту →
          </button>
        </div>
      )}
    </aside>
  );
}

function pluralHw(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'домашка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'домашки';
  return 'домашек';
}

// ── ритм недели ───────────────────────────────────────────────────────

export interface RhythmDay {
  dateKey: string;
  /** 0-4 как HeatCell.level */
  level: 0 | 1 | 2 | 3 | 4;
  /** день ещё не наступил (пунктир) */
  future: boolean;
  today: boolean;
  /** «пн · 25 мин» */
  title: string;
}

export interface WeekRhythmProps {
  days: RhythmDay[];
  streak: number;
  weekDone: number;
  weekGoal: number;
  goalMin: number;
  activeMin: number;
  caption: string;
}

export function WeekRhythm({
  days,
  streak,
  weekDone,
  weekGoal,
  goalMin,
  activeMin,
  caption,
}: WeekRhythmProps) {
  const pct = goalMin > 0 ? Math.min(100, Math.round((activeMin / goalMin) * 100)) : 0;
  return (
    <section className="dwr-card" data-testid="week-rhythm" aria-label="Ритм недели">
      <div className="dwr-lab">Ритм недели</div>
      <div className="dwr-days" role="img" aria-label={`Активность за неделю: ${weekDone} из ${weekGoal} дней`}>
        {days.map((d) => (
          <span
            key={d.dateKey}
            className={`dwr-d l${d.level}${d.future ? ' off' : ''}${d.today ? ' today' : ''}`}
            title={d.title}
          />
        ))}
      </div>
      <div className="dwr-meta">
        <span className="dwr-chip">стрик {streak} {pluralDaysShort(streak)}</span>
        <span className="dwr-chip">неделя {weekDone}/{weekGoal}</span>
      </div>
      <div className="dwr-goal">
        <span className="dwr-goal-lab">цель дня</span>
        <div className="dwr-bar" aria-hidden="true">
          <i style={{ width: `${pct}%` }} />
        </div>
        <span className="dwr-goal-n">
          {activeMin}/{goalMin} мин
        </span>
      </div>
      <div className="dwr-caption" data-testid="dwr-caption">
        {caption}
      </div>
    </section>
  );
}

function pluralDaysShort(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}
